"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { rpc } from "#rpc";

const useDebouncedValue = <T>(value: T, delay: number): T => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(id);
	}, [value, delay]);
	return debounced;
};

export const useSearchResults = (query: string) => {
	const trimmed = query.trim();
	const debounced = useDebouncedValue(trimmed, 200);

	const queryResult = useQuery({
		queryKey: ["search-results", debounced],
		queryFn: async () => {
			if (!debounced) {
				return {
					products: [],
					relatedCategories: [],
					relatedPages: [],
					relatedPagesCount: 0,
					relatedFaqs: [],
					didYouMean: null,
					nHits: 0,
				};
			}
			return rpc.search.results({ query: debounced, limit: 12 });
		},
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

	return {
		...queryResult,
		// True while the live input has typed-ahead of the debounced value (the
		// query hasn't fired yet for the latest keystrokes). Consumers can use
		// this to suppress no-results UI during the debounce window so it
		// doesn't flash between keystrokes.
		isDebouncing: trimmed !== debounced,
	};
};

export const usePopularProducts = () => {
	return useQuery({
		queryKey: ["search-popular"],
		queryFn: async () => rpc.search.popular({ limit: 8 }),
		staleTime: 5 * 60_000,
	});
};

export const usePopularCategories = () => {
	return useQuery({
		queryKey: ["search-popular-categories"],
		queryFn: async () => rpc.search.popularCategories({ limit: 6 }),
		staleTime: 5 * 60_000,
	});
};
